<script setup lang="ts">
import AppCheckbox from '@/components/ui/AppCheckbox.vue'
import AppButton from '@/components/ui/AppButton.vue'
import Loading from '@/components/ui/Loading.vue'
import AppointmentDateTimePicker from '@/components/ui/AppointmentDateTimePicker.vue'
import Modal from '@/components/ui/Modal.vue'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useRemoteDiagnose } from '@/stores/remote-diagnose'
import { pinia } from '@/stores'
import { storeToRefs } from 'pinia'
import { useRouterStore } from '@/stores/router'
import type { Language } from '@/types/remote-diagnose'
import { updateRemoteDiagnose } from '@/services/remote-diagnose'
import { validateQuestionLength, validateMileage } from '@/utils/remote-diagnose'
import { getPaymentRoute } from '@/utils/router'
import { useLang } from '@/composables/use-lang'
import ScheduleAppointmentContent from '@/lang/schedule-appointment.json'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  }
})

const remoteDiagnoseState = useRemoteDiagnose(pinia)
const { current, push } = useRouterStore(pinia)
const { state } = storeToRefs(remoteDiagnoseState)

const hasJobcardId = ref<boolean>(false)
const error = ref<boolean>(false)
const loading = ref<boolean>(false)
const loadingPage = ref<boolean>(true)
const questionLengthError = ref<boolean>(false)
const mileageError = ref<boolean>(false)
const paymentId = ref<string>('')
const projectId = ref<string>('')
const modal = ref<typeof Modal>()
const appointmentDatePicker = ref<typeof AppointmentDateTimePicker>()

const langContent = useLang(ScheduleAppointmentContent)

const hasLanguage = computed(() => {
  return !!state.value.language.length
})

watch(
  () => state.value.question,
  (newValue) => {
    if (!newValue) return
    questionLengthError.value = validateQuestionLength(newValue)
  }
)

const emit = defineEmits(['scroll'])

const validateInputs = () => {
  let hasError = false
  if (!hasLanguage.value) hasError = true
  if (!state.value.date) {
    appointmentDatePicker.value?.setDateError()
    hasError = true
  }
  if (!state.value.time) {
    appointmentDatePicker.value?.setTimeError()
    hasError = true
  }
  if (!state.value.question || validateQuestionLength(state.value.question)) {
    questionLengthError.value = true
    hasError = true
  }
  if (validateMileage(state.value.client_mileage_state)) {
    mileageError.value = true
    hasError = true
  }
  return !hasError
}

watch(
  () => state.value.client_mileage_state,
  (value: string) => {
    mileageError.value = validateMileage(value)
  }
)

const updateJobcard = async () => {
  loading.value = true

  if (!validateInputs()) {
    loading.value = false
    return
  }

  const formData = {
    payment_id: paymentId.value,
    name: projectId.value,
    date: state.value.date,
    time: state.value.time,
    language: state.value.language,
    question: state.value.question,
    dongle_tested: state.value.dongle_tested,
    client_mileage_state: state.value.client_mileage_state
  }

  const result = await updateRemoteDiagnose(formData, props.aws_url)

  if (result.data) {
    loading.value = false
    push('schedule-appointment-result')
  }

  error.value = true
  loading.value = false
}

const handleChange = (e: Event) => {
  const el = e.target as HTMLInputElement

  state.value.language = el.checked
    ? ([...state.value.language, el.value] as Language[])
    : state.value.language.filter((lang) => lang !== el.value)
}

const isChecked = (e: string) => {
  return Boolean(state.value.language.find((lang) => lang === e))
}

const goPayment = () => {
  if (!validateInputs()) return
  push(getPaymentRoute(state.value.payment_gateway))
}

const handleUpdateDate = (selectedDate: string) => {
  state.value.time = ''
  state.value.date = selectedDate
}

const pageLoaded = () => {
  loadingPage.value = false
}

const setDongleTested = () => {
  state.value.dongle_tested = true
  modal.value?.close()
}

const lang: Language = inject('lang') ?? 'nl'

onMounted(async () => {
  emit('scroll')
  if (lang) {
    state.value.language = [lang]
  }

  if (current.params.delivered === '1') {
    hasJobcardId.value = true
    paymentId.value = current.params.paymentId
    projectId.value = current.params.projectId
    modal.value?.open()
  }
})
</script>

<template>
  <Loading v-if="loadingPage" />
  <form
    v-if="langContent"
    @submit.prevent="updateJobcard"
    class="flex flex-col items-center text-center"
  >
    <p class="mb-10">{{ langContent.choose_languages }}</p>

    <div
      :class="`grid grid-cols-2 md:grid-cols-3 md:gap-4 items-center ${!hasLanguage ? 'mb-0' : 'mb-10'}`"
    >
      <app-checkbox
        v-for="lang in langContent.languages"
        :key="lang"
        :label="lang.label"
        :value="lang.value"
        @input="handleChange"
        :checked="isChecked(lang.value)"
      />
    </div>
    <span v-if="!hasLanguage" class="text-english-vermillion mb-5">{{
      langContent.language_error
    }}</span>

    <appointment-date-time-picker
      ref="appointmentDatePicker"
      :aws_url="aws_url"
      v-model="state.time"
      @updateDate="handleUpdateDate"
      @pageLoaded="pageLoaded"
    ></appointment-date-time-picker>

    <input
      :placeholder="langContent.mileage_label"
      type="text"
      :class="mileageError ? 'border-english-vermillion border' : ''"
      v-model="state.client_mileage_state"
    />
    <span v-if="mileageError" class="text-english-vermillion">
      {{ langContent.mileage_error }}
    </span>

    <p class="mt-10">{{ langContent.question_label }}</p>

    <textarea
      v-model="state.question"
      :class="`mt-10 h-24 ${questionLengthError ? 'border-english-vermillion border' : ''}`"
    ></textarea>
    <span class="text-english-vermillion" v-if="questionLengthError">
      {{ langContent.question_error }}
    </span>

    <p v-if="error" class="text-english-vermillion">
      {{ langContent.error_scheduling }}
      <a class="font-bold" href="https://wa.me/31638746918"> +31 6 38746918</a>
    </p>

    <app-button type="button" class="mt-10" v-if="!hasJobcardId" @click="goPayment">{{
      langContent.go_to_payment
    }}</app-button>
    <app-button v-else type="submit" class="mt-10" :loading="loading">{{
      langContent.continue
    }}</app-button>

    <modal ref="modal" :hasCloseButton="false">
      <p>
        {{ langContent.dongle_modal_text }}
        <a class="font-bold text-english-vermillion outline-none capitalize underline" :href="langContent.instructions_link" target="_blank"
          >{{ langContent.instructions }}</a
        >. {{ langContent.dongle_modal_text2 }}
      </p>

      <div class="flex justify-evenly mt-10">
        <app-button type="button" @click="setDongleTested">{{
          langContent.dongle_tested_button
        }}</app-button>
      </div>
    </modal>
  </form>
</template>

<style scoped></style>
